import {useEffect, useState} from "react";
import {
  Phone,
  User,
  Mail,
  MapPin,
  Tag,
  Search,
  PhoneOutgoing,
} from "lucide-react";
import moment from "moment-timezone";

/* --- SUB-COMPONENTS --- */

const DetailItem = ({icon: Icon, label, value}) => (
  <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg shadow-sm">
    <Icon className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#2ba9cd]" />
    <div className="flex flex-col">
      <span className="text-xs font-semibold uppercase text-gray-500">
        {label}
      </span>
      <span className="text-sm font-medium text-gray-800 break-words">
        {value || "--"}
      </span>
    </div>
  </div>
);

const ViewDetails = ({selectedItem, setIsModalOpen}) => {
  if (!selectedItem) return null;
  return (
    <div className="flex flex-col bg-white overflow-y-auto p-4">
      <div className="flex justify-between items-start border-b pb-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-1">
            {selectedItem.customer_firstname} {selectedItem.customer_lastname}
          </h1>
          <p className="flex items-center text-lg text-[#2ba9cd] font-mono">
            {selectedItem.customer_phonenumber}
            <PhoneOutgoing
              className="w-5 h-5 ml-2 cursor-pointer text-red-500"
              onClick={() => setIsModalOpen(true)}
            />
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <DetailItem
          icon={User}
          label="First Name"
          value={selectedItem.customer_firstname}
        />
        <DetailItem
          icon={User}
          label="Last Name"
          value={selectedItem.customer_lastname}
        />
        <DetailItem
          icon={Mail}
          label="Email"
          value={selectedItem.customer_email}
        />
        <DetailItem
          icon={Tag}
          label="Program"
          value={selectedItem.client_name?.toLowerCase()}
        />
      </div>

      <h3 className="text-xl font-semibold text-gray-700 mt-4 mb-3 border-b pb-1">
        Location
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <DetailItem
          icon={MapPin}
          label="Address"
          value={selectedItem.customer_address}
        />
        <DetailItem
          icon={MapPin}
          label="City"
          value={selectedItem.customer_city}
        />
        <DetailItem
          icon={MapPin}
          label="State"
          value={selectedItem.customer_state}
        />
        <DetailItem
          icon={MapPin}
          label="Country"
          value={selectedItem.customer_country}
        />
      </div>

      <h3 className="text-xl font-semibold text-gray-700 mt-4 mb-3 border-b pb-1">
        Contact Id
      </h3>
      <div className="p-4 bg-[#E7F7FB] rounded-lg border border-[#2ba9cd]">
        <div className="flex items-center justify-start text-sm text-gray-700">
          <Phone className="w-4 h-4 mr-2 text-[#2ba9cd] flex-shrink-0" />
          <p>{selectedItem.ContactId}</p>
        </div>
      </div>
    </div>
  );
};

const MissedList = ({
  data,
  searchTerm,
  selectedId,
  setSearchTerm,
  onSelect,
}) => (
  <div className="flex flex-col bg-white border-r border-gray-200 h-full">
    <div className="p-4 border-b flex-shrink-0">
      <h2 className="text-xl font-bold text-gray-800 flex items-center">
        <Phone className="w-5 h-5 mr-2 text-[#2ba9cd]" />
        Missed Directory
      </h2>
      <div className="relative mt-2 mb-3">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name, phone, or program..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>
    </div>
    <div className="flex-1 min-h-0 overflow-y-auto">
      {data?.length > 0 ? (
        data.map((item) => (
          <div
            key={item.ContactId}
            className={`px-3 py-2 space-y-1 cursor-pointer transition border-b last:border-b-0
              ${
                selectedId === item.ContactId
                  ? "bg-[#E7F7FB] border-[#2ba9cd] border-l-4"
                  : "hover:bg-gray-50 border-l-4 border-[#E7F7FB]"
              }`}
            onClick={() => onSelect(item)}
          >
            <div className="flex space-x-3 justify-between items-center">
              <div className="flex flex-col">
                <p className="text-sm text-gray-900">
                  {item.start_time
                    ? moment
                        .tz(item.start_time, "America/Los_Angeles")
                        .format("MM/DD/YYYY hh:mm A")
                    : "--"}
                </p>
                <p className="text-xs text-gray-400">
                  {item.customer_phonenumber}
                </p>
              </div>
              <div className="flex flex-col items-end">
                {item.highlighted && (
                  <div className="h-2 w-2 bg-red-600 rounded-full shadow-[0_0_8px_rgba(220,38,38,0.5)]" />
                )}
              </div>
            </div>
          </div>
        ))
      ) : (
        <p className="p-4 text-center text-gray-500 text-sm italic">
          {searchTerm
            ? `No matches for "${searchTerm}".`
            : "No interactions found."}
        </p>
      )}
    </div>
  </div>
);

const MissedPane = ({selectedItem, setIsModalOpen}) => {
  if (selectedItem) {
    return (
      <ViewDetails
        selectedItem={selectedItem}
        setIsModalOpen={setIsModalOpen}
      />
    );
  }
  return (
    <div className="flex h-full items-center justify-center text-gray-500">
      <p className="text-lg">Select a missed interaction from the list.</p>
    </div>
  );
};

const OrbitLoader = () => (
  <div className="relative h-10 w-10 animate-spin">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#2ba9cd] rounded-full"></div>
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
  </div>
);

/* --- MAIN COMPONENT --- */

const MissedCalls = (props) => {
  const [missedCalls, setMissedCalls] = useState([]);
  const [selectedMissedCall, setSelectedMissedCall] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState("");

  // Track loading and whether the first-ever load has occurred
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [queueOptions, setQueueOptions] = useState([
    {value: "", name: "--Please Select--"},
  ]);

  // Filter Logic
  const filterData = (data) => {
    if (!searchTerm) return data;
    const lowerCaseSearch = searchTerm?.toLowerCase();
    return data.filter(
      (item) =>
        item.customer_phonenumber?.toLowerCase().includes(lowerCaseSearch) ||
        item.customer_firstname?.toLowerCase().includes(lowerCaseSearch) ||
        item.customer_lastname?.toLowerCase().includes(lowerCaseSearch) ||
        item.client_name?.toLowerCase().includes(lowerCaseSearch),
    );
  };

  const filteredCalls = filterData(missedCalls);

  const getMissedInteractions = async () => {
    // Only show the spinner if this is the first time loading
    if (isInitialLoad) {
      setIsLoading(true);
    }

    try {
      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_AUTH_TOKEN,
        },
      };

      const callsRes = await fetch(
        `${process.env.REACT_APP_AWS_ENDPOINT}/get-missedcalls`,
        options,
      );
      const callData = await callsRes.json();

      let queues =
        props.agentQueues?.map((x) => x?.name?.toLowerCase()).filter(Boolean) ||
        [];

      const processData = (data) => {
        // A record with no client_name at all (e.g. a contact whose flow
        // never invoked amazonconnect-addcall) can never match any agent's
        // queue name - so filtering strictly by queues.includes(...) was
        // silently hiding those from every agent, missed calls included.
        // Only apply the queue filter to records that actually have a
        // client_name; let anything unattributed through instead of
        // dropping it.
        let filtered =
          queues.length > 0
            ? data.filter((x) => {
                const clientName = x.client_name?.toLowerCase();
                return !clientName || queues.includes(clientName);
              })
            : data;
        return filtered.sort(
          (a, b) => new Date(b.start_time) - new Date(a.start_time),
        );
      };

      const processedCallData = processData(callData);

      const updatedCallData = processedCallData.map((call) => ({
        ...call,
        highlighted: props.notifications.missedVoice.some(
          (n) => n.ContactId === call.ContactId,
        ),
      }));

      setMissedCalls(updatedCallData);

      // Successfully loaded data once, so disable initial load state
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    } catch (error) {
      console.error("Error fetching interactions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (props.agentQueues?.length > 0) {
      setQueueOptions([
        {value: "", name: "--Please Select--"},
        ...props.agentQueues.filter(Boolean),
      ]);
    }
  }, [props.agentQueues]);

  useEffect(() => {
    getMissedInteractions();
  }, [props.agentQueues, props.notifications]);

  // Auto-select first item when list changes or filter is applied
  useEffect(() => {
    const exists = filteredCalls.some(
      (c) => c.ContactId === selectedMissedCall?.ContactId,
    );
    if (!exists || !selectedMissedCall) {
      setSelectedMissedCall(filteredCalls.length > 0 ? filteredCalls[0] : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCalls]);

  const handleSubmit = () => {
    setIsModalOpen(false);
    props.makeDirectOutboundCall(
      selectedMissedCall?.customer_phonenumber,
      selectedQueue,
    );
  };

  const handleItemSelect = (item) => {
    setSelectedMissedCall(item);

    if (
      item.ContactId ===
      props.notifications.missedVoice.find(
        (n) => n.ContactId === item.ContactId,
      )?.ContactId
    ) {
      const options = {
        method: "PATCH",
        body: JSON.stringify({
          ContactId: item.ContactId,
          source_notification: "MissedVoice",
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_AUTH_TOKEN,
        },
      };
      fetch(`${process.env.REACT_APP_AWS_ENDPOINT}/notifications`, options)
        .then((resp) => resp.json())
        .then(() => props.onNotificationRead?.())
        .catch((error) => console.error("Error marking read:", error));
    }
  };

  return (
    <div className="flex flex-col p-4 space-y-6 h-full">
      <div className="flex justify-between p-4 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold text-gray-800">Missed Calls</h2>
      </div>

      {/* Show loader only on the first load; hide it for background refreshes */}
      {!isLoading ? (
        <div className="flex flex-1 min-h-0 bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="w-1/3 flex flex-col">
            <MissedList
              data={filteredCalls}
              searchTerm={searchTerm}
              selectedId={selectedMissedCall?.ContactId}
              setSearchTerm={setSearchTerm}
              onSelect={handleItemSelect}
            />
          </div>
          <div className="w-2/3 bg-white flex flex-col">
            <MissedPane
              selectedItem={selectedMissedCall}
              setIsModalOpen={setIsModalOpen}
            />
          </div>

          {isModalOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
              onClick={() => setIsModalOpen(false)}
            >
              <div
                className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-2xl font-semibold mb-4">Outbound Call</h3>
                <div className="space-y-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium mb-1">
                      Target Queue
                    </label>
                    <select
                      value={selectedQueue}
                      onChange={(e) => setSelectedQueue(e.target.value)}
                      className="p-2 border rounded-lg"
                    >
                      {queueOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2 bg-gray-200 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={!selectedQueue}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50"
                    >
                      Call
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex h-full justify-center items-center">
          <OrbitLoader />
        </div>
      )}
    </div>
  );
};

export default MissedCalls;
